/**
 * UserMenu — menu de conta do header (GUPM-212), no padrão do menu do GitHub.
 *
 * Gatilho: ícone de perfil (iniciais) + seta. Ao abrir, mostra nome/email/perfil
 * e a lista de ações que antes ficavam soltas no header (Autenticador/TOTP e Sair)
 * + Termos de Uso / Política de Privacidade (desabilitados até as telas existirem).
 *
 * Acessível (padrão "menu button" WAI-ARIA): `aria-haspopup="menu"`,
 * `role="menu"`/`menuitem`, foco vai pro 1º item ao abrir, navegação por
 * ↑/↓ entre os itens acionáveis, fecha no Esc (foco volta ao gatilho) e ao clicar fora.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UserProfile } from '@/hooks/useAuth';
import { usePopup } from '@/hooks/usePopup';
import { useUserIdentity } from './useUserIdentity';

const PROFILE_LABEL: Record<UserProfile, string> = {
  operador: 'Operador',
  tecnico_responsavel: 'Técnico Responsável',
  admin: 'Administrador',
};

const ITEM_BASE =
  'flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] transition-colors';

/** Iniciais do nome (ex.: "Ana Engenheira" → "AE"); null sem nome. */
function initials(name: string | null): string | null {
  if (!name) return null;
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || null;
}

interface MenuItemProps {
  icon: string;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
}

function MenuItem({ icon, label, onClick, disabled, danger }: MenuItemProps) {
  const tone = disabled
    ? 'cursor-not-allowed text-jf-dim opacity-60'
    : danger
      ? 'text-red-300 hover:bg-red-500/10 hover:text-red-200'
      : 'text-jf-muted hover:bg-white/[0.06] hover:text-white';
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      title={disabled ? 'Em breve' : undefined}
      onClick={onClick}
      className={`${ITEM_BASE} ${tone}`}
    >
      <span aria-hidden className="text-base">{icon}</span>
      <span className="flex-1">{label}</span>
    </button>
  );
}

export default function UserMenu() {
  const navigate = useNavigate();
  const { name, email, profile, signOut } = useUserIdentity();
  const [open, setOpen] = useState(false);
  const [erroSaida, setErroSaida] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // Esc fecha e devolve o foco ao gatilho. Pela pilha de popups (C-91): com um popup
  // aberto por cima, o Esc é dele e o menu não fecha junto. Sem prender o Tab: é menu.
  usePopup({
    aberto: open,
    aoFechar: () => {
      setOpen(false);
      triggerRef.current?.focus();
    },
    prenderFoco: false,
    gerirFoco: false,
  });

  // Padrão "menu button": ao abrir, foca o 1º item acionável; ↑/↓ navegam entre
  // eles (itens desabilitados são pulados); clique fora fecha. Listeners só enquanto
  // aberto — sem ociosos, sem race de abre-e-fecha (o gatilho vive dentro de rootRef).
  useEffect(() => {
    if (!open) return;
    const items = () =>
      Array.from(
        menuRef.current?.querySelectorAll<HTMLButtonElement>(
          '[role="menuitem"]:not([disabled])',
        ) ?? [],
      );
    items()[0]?.focus();

    function onPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const list = items();
        if (list.length === 0) return;
        const idx = list.indexOf(document.activeElement as HTMLButtonElement);
        const next =
          e.key === 'ArrowDown'
            ? (idx + 1) % list.length
            : (idx - 1 + list.length) % list.length;
        list[next]?.focus();
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const initialsText = initials(name);
  const profileLabel = profile ? PROFILE_LABEL[profile] : null;

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  /**
   * Sai — e só fecha o menu se a saída de fato ocorreu (GUPM-255).
   *
   * O `signOut` pode falhar sem encerrar a sessão: o auth-js retorna antes de
   * limpar o `localStorage` quando o `POST /logout` cai. Fechar o menu nesse
   * caso seria afirmar algo falso a quem está tentando sair — e no posto de
   * vigilância, com dispositivo compartilhado, o turno seguinte agiria dentro
   * da conta anterior e o `audit_log` sairia assinado errado.
   */
  async function handleSair() {
    setErroSaida(null);
    const { error } = await signOut();
    if (error) {
      setErroSaida(
        'Não foi possível sair: você continua conectado. Verifique a conexão e tente novamente.',
      );
      return;
    }
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative flex-shrink-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Menu de usuário"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        title={name ?? email ?? 'Conta'}
        className="flex items-center gap-1 rounded-lg p-1 pr-1.5 text-jf-muted hover:bg-white/10 hover:text-white transition-colors"
      >
        <span
          aria-hidden
          className="flex h-7 w-7 items-center justify-center rounded-full bg-jf-yellow/15 text-[11px] font-bold text-jf-yellow ring-1 ring-jf-yellow/30"
        >
          {initialsText ?? '👤'}
        </span>
        <svg
          aria-hidden
          viewBox="0 0 12 12"
          // C-24: abaixo de 420px a seta sai para o sino caber no header a 320px; as
          // iniciais seguem como gatilho (o aria-label já diz que é menu).
          className={`hidden min-[420px]:block h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
        >
          <path d="M3 4.5 6 7.5 9 4.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          aria-label="Menu de usuário"
          className="absolute right-0 top-full z-50 mt-1.5 w-64 overflow-hidden rounded-xl border border-jf-border bg-jf-surface2 shadow-xl shadow-black/40"
        >
          {/* Cabeçalho: nome / email / perfil */}
          <div className="flex items-center gap-3 border-b border-jf-border px-3 py-3">
            <span
              aria-hidden
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-jf-yellow/15 text-[13px] font-bold text-jf-yellow ring-1 ring-jf-yellow/30"
            >
              {initialsText ?? '👤'}
            </span>
            <div className="min-w-0">
              <p className="truncate text-[13px] font-bold text-white">{name ?? 'Usuário'}</p>
              {email && <p className="truncate text-[11px] text-jf-muted">{email}</p>}
              {profileLabel && (
                <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-jf-yellow/80">
                  {profileLabel}
                </p>
              )}
            </div>
          </div>

          {/* Ações de conta */}
          <div className="py-1">
            <MenuItem icon="🔐" label="Autenticador (TOTP)" onClick={() => go('/perfil/totp')} />
          </div>

          {/* Legais — desabilitados até as telas existirem (rotas já criadas) */}
          <div className="border-t border-jf-border py-1">
            <MenuItem icon="🔒" label="Termos de Uso" disabled />
            <MenuItem icon="🔒" label="Política de Privacidade" disabled />
          </div>

          {/* Sair */}
          <div className="border-t border-jf-border py-1">
            <MenuItem
              icon="⏻"
              label="Sair"
              danger
              onClick={() => { void handleSair(); }}
            />
            {erroSaida && (
              <p
                role="alert"
                className="mx-2 mt-1 mb-2 rounded-md border border-red-500/40 bg-red-500/10 px-2.5 py-2 text-xs leading-snug text-red-300"
              >
                {erroSaida}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
